import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';

// ─────────────────────────────────────────────────────────────────────────────
// THE NATIVE CALL ENGINE — the web half.
//
// Stage 1 is the CallKit lifecycle only. The media is still the WebView's
// WebRTC peer (src/api/voiceDaemon.ts); this tells iOS that a call exists so it
// gets a real call's treatment — lock-screen controls and proper interruption
// handling. Backgrounding is stage 2, the media move is stage 3.
//
// ── THE RAIL ────────────────────────────────────────────────────────────────
// iOS must never show a call state the box does not hold. `startCall` is
// therefore NOT called on the tap. The tap starts the session; only when the
// box confirms — the voice daemon leaving `connecting`, which happens on
// voice:session_ready or on the transport reporting connected — does anything
// reach CallKit. The sphere follows the same signal: the colour follows the
// box, not the finger.
//
// ── INERT OFF-DEVICE ────────────────────────────────────────────────────────
// This surface also runs in a desktop browser at /?surface=phone. There is no
// CallKit there, and a missing bridge must not break the call that works today,
// so every call through here is a no-op unless the platform is genuinely
// native. That check is `Capacitor.isNativePlatform()`, not a user-agent guess.
// ─────────────────────────────────────────────────────────────────────────────

export type NativeCallEvent =
  | 'ringing' | 'connected' | 'ended' | 'muted' | 'audioRouteChanged'
  // Emitted from CXProviderDelegate's didActivate/didDeactivate. A callback
  // that silently did not fire is indistinguishable from one that did — which
  // is what made build 10 undiagnosable — so it now says so out loud.
  | 'audioActivated' | 'audioDeactivated'
  // A ROUTE THAT REFUSED TO MOVE, AND ONE HELD BACK UNTIL THE HANDOVER (build
  // 21, Ghost finding 3). Swift has emitted `audioRouteFailed` since the route
  // setter existed; this union did not list it and nothing subscribed, so a
  // refused route was silent — exactly the state that would have explained a
  // silent call, going unsaid. `audioRouteDeferred` is its pre-activation twin:
  // recorded, not applied, and saying so rather than reporting a route that moved.
  | 'audioRouteFailed' | 'audioRouteDeferred';

// CXErrorDomainRequestTransaction's codes. CallKit refuses a transaction with
// one of these and nothing else, so the number IS the diagnosis — mapped here
// rather than in Swift, where getting the enum's shape wrong would be a
// compile failure for no benefit.
const CX_TRANSACTION_ERRORS: Record<number, string> = {
  0: 'unknown',
  1: 'unentitled',
  2: 'unknownCallProvider',
  3: 'emptyTransaction',
  4: 'unknownCallUUID',
  5: 'callUUIDAlreadyExists',
  6: 'invalidAction',
  7: 'maximumCallGroupsReached',
};

/**
 * Turn a rejection into the underlying reason. The plugin puts
 * `<domain>#<code> — <localizedDescription>` in the message precisely so this
 * can read it without depending on how Capacitor maps its error fields.
 */
export function describeNativeCallError(e: unknown): string {
  const raw = String((e as Error)?.message ?? e);
  const m = raw.match(/([A-Za-z0-9_.]+)#(-?\d+)/);
  if (!m) return raw.slice(0, 90);
  const domain = m[1];
  const code = Number(m[2]);
  const named = /requesttransaction/i.test(domain)
    ? CX_TRANSACTION_ERRORS[code]
    : undefined;
  const dash = raw.indexOf('—');
  const reason = dash >= 0 ? raw.slice(dash + 1).trim() : '';
  const head = named ? `${named}(${code})` : `${domain}#${code}`;
  return reason ? `${head} · ${reason}` : head;
}

export interface NativeCallDiagnostics {
  hasCall: boolean;
  callId: string;
  reportedConnected: boolean;
  audioActive: boolean;
  /** `'untouched'` — NOT false — when this process has never contacted
   *  RTCAudioSession. The two are different facts and the type says so, because
   *  a boolean here would let `untouched` read as `manual=n`, and "never
   *  contacted" is now the normal state before the first call. */
  manualAudio: boolean | 'untouched';
  /** THE EPOCH the native side is gating on (Q2). Exposed so the surface reads
   *  the same value that decides whether an activation is honoured, rather than
   *  keeping a parallel count that can disagree with it. */
  callEpoch?: number;
  /** Present and true only on a binary that carries the stage-3b media engine.
   *  ABSENT on every older build — which is the point: the JS half ships from
   *  the box at runtime and the Swift does not, so the two can be out of step by
   *  a whole release. */
  mediaEngine?: boolean;
  /** Present and true only on a binary that STATES ITS OWN PLAYBACK over the
   *  peer's control channel (Fix 2). Deliberately NOT the same flag as
   *  `mediaEngine`: a 3b binary answers true to that one and carries no
   *  `playing` verb at all, so gating the web-side suppression on it would let
   *  a RELOAD leave nobody asserting playback — every mark un-acked, the whole
   *  reply missing from the transcript, on a binary nobody rebuilt. */
  playbackVerb?: boolean;
  audioEnabled: boolean | 'untouched';
  /** `live` (the contract is established, so reading adds no contact that has
   *  not already happened) or `untouched` (nothing was read at all). */
  audioFlagsSource?: 'live' | 'untouched';
  /** Has the audio contract been established yet this launch — false before the
   *  first call, true after. What makes the two flags above real or `untouched`. */
  audioTouched?: boolean;
  /** Provenance of the CXProvider — settles the "wrong thread / too late"
   *  candidates with a recorded fact rather than an assumption. */
  providerBuiltOnMain: boolean;
  providerAgeSeconds: number;
  handleTypeUsed: string;
  /** Calls the system already associates with this app. Non-zero before a call
   *  has started is itself the diagnosis. */
  systemCalls: number;
}

export interface NativeCallPlugin {
  /** Proof of life. Resolving proves the native class was found and answered. */
  getDiagnostics(): Promise<NativeCallDiagnostics>;
  /** Tell iOS a call exists and is CONNECTING. Resolves with the CallKit id AND
   *  THE EPOCH it was issued under — the id alone cannot say which generation it
   *  belongs to, so media start could not be gated on the same value native uses
   *  to decide whether an activation is honoured. */
  startCall(options: { displayName: string }): Promise<{ callId: string; epoch: number }>;
  /** The peer is up and media can flow. A separate fact, reported separately. */
  reportConnected(): Promise<void>;
  setMuted(options: { muted: boolean }): Promise<void>;
  /** THE ROUTE (build 19). `speaker` is required — a defaulted route is a route
   *  nobody chose, and this control exists because that was the problem. */
  setAudioRoute(options: { speaker: boolean }): Promise<void>;
  endCall(): Promise<void>;
  addListener(
    event: NativeCallEvent,
    fn: (data: any) => void,
  ): Promise<PluginListenerHandle>;
}

const plugin = registerPlugin<NativeCallPlugin>('NativeCall');

/** True only on a real device build that carries the plugin. */
export function nativeCallAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('NativeCall');
}

/** Is this a native shell at all, as opposed to a desktop browser? */
export function isNativeShell(): boolean {
  return Capacitor.isNativePlatform();
}

/** Does the JS bridge hold a registration for the plugin? */
export function nativeCallRegistered(): boolean {
  return Capacitor.isPluginAvailable('NativeCall');
}

/**
 * ASK NATIVE DIRECTLY. The two booleans above are what the JS side BELIEVES;
 * this is the only thing that proves the native class exists, was found via
 * NSClassFromString off the generated packageClassList, and answered. A
 * rejection here is the single most useful fact this surface can report,
 * because it separates "the plugin is not in the binary" from "the plugin is
 * there and CallKit refused".
 */
export async function nativeCallDiagnostics(): Promise<NativeCallDiagnostics> {
  return plugin.getDiagnostics();
}

/**
 * Every wrapper below reports its outcome rather than swallowing it. A CallKit
 * refusal is a real event: if iOS declines the call, the surface must not go on
 * believing it has one.
 */
/** THE CALLKIT CALL AND THE EPOCH IT BELONGS TO (Q2).
 *
 *  Returns null off-device. On device the epoch travels with the id, because a
 *  surface holding only an id cannot tell a live call from one whose activation
 *  was superseded — and stage 3b gates MEDIA START on this value, so it has to
 *  cross the bridge rather than be inferred on either side. */
export interface NativeCallStart {
  callId: string;
  epoch: number;
}

export async function startNativeCall(displayName: string): Promise<NativeCallStart | null> {
  if (!nativeCallAvailable()) return null;
  const { callId, epoch } = await plugin.startCall({ displayName });
  return { callId, epoch: Number(epoch ?? 0) };
}

/**
 * Media is flowing. Keyed off the WebRTC connection state, never off the box
 * accepting the session — session acceptance and an established peer are
 * different facts, and CallKit is told each one only when it is true.
 */
export async function reportNativeCallConnected(): Promise<void> {
  if (!nativeCallAvailable()) return;
  await plugin.reportConnected();
}

export async function setNativeCallMuted(muted: boolean): Promise<void> {
  if (!nativeCallAvailable()) return;
  await plugin.setMuted({ muted });
}

/**
 * Move the call between the speaker and the receiver.
 *
 * THE MECHANISM IS THE CATEGORY OPTION, NOT AN OVERRIDE, and that is not an
 * implementation detail the caller may ignore: `overrideOutputAudioPort(.none)`
 * returns to the NORMAL route, and with `.defaultToSpeaker` set the normal route
 * IS the speaker — so an override-based "speaker off" does nothing. The native
 * side owns the whole mechanism; this passes the intent and nothing else.
 *
 * The button must render from the MEASURED route (`audioRouteChanged`), never
 * from the tap or from this promise resolving.
 */
export async function setNativeAudioRoute(speaker: boolean): Promise<void> {
  if (!nativeCallAvailable()) return;
  await plugin.setAudioRoute({ speaker });
}

export async function endNativeCall(): Promise<void> {
  if (!nativeCallAvailable()) return;
  await plugin.endCall();
}

export async function onNativeCallEnded(
  fn: (data: { callId: string; reason: string }) => void,
): Promise<PluginListenerHandle | null> {
  if (!nativeCallAvailable()) return null;
  return plugin.addListener('ended', fn);
}

export async function onNativeCallEvent(
  event: NativeCallEvent,
  fn: (data: any) => void,
): Promise<PluginListenerHandle | null> {
  if (!nativeCallAvailable()) return null;
  return plugin.addListener(event, fn);
}
